import React, { useMemo, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { Sprint } from '@marketing-milk/interfaces'

type Props = {
  sprints: Sprint[]
  disabledSprintIDs: Set<number>
  onCreate: (sprintID: number) => Promise<void>
  onClose: () => void
}

const formatEpochDate = (epochSeconds: number) =>
  new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

export function NewSprintPacketModal({ sprints, disabledSprintIDs, onCreate, onClose }: Props) {
  const [sprintID, setSprintID] = useState<number | ''>('')
  const [saving, setSaving] = useState(false)

  const sorted = useMemo(() => [...sprints].sort((a, b) => b.startTime - a.startTime), [sprints])

  const handleCreate = async () => {
    if (typeof sprintID !== 'number') return
    setSaving(true)
    try {
      await onCreate(sprintID)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">New Sprint Packet</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pick an existing sprint to build a packet around.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
          >
            <i className="fa fa-times text-lg" />
          </button>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-700">Sprint *</label>
          <select
            value={sprintID === '' ? '' : String(sprintID)}
            onChange={e => setSprintID(e.target.value ? Number(e.target.value) : '')}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            <option value="">Select a sprint…</option>
            {sorted.map(s => {
              const disabled = disabledSprintIDs.has(s.id)
              return (
                <option key={s.id} value={s.id} disabled={disabled}>
                  {s.name} ({formatEpochDate(s.startTime)} – {formatEpochDate(s.endTime)})
                  {disabled ? ' — already has a packet' : ''}
                </option>
              )
            })}
          </select>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={typeof sprintID !== 'number' || saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Create packet'}
          </button>
        </div>
      </div>
    </div>
  )
}
